import { Box, Button, Stack, ThemeSpaceVariable, useDisclosure } from "@caspeco/casper-ui-library";
// eslint-disable-next-line caspeco-react/discourage-chakra-import
import { ScaleFade, useMediaQuery } from "@chakra-ui/react";
import { useCancelBooking } from "api/api-hooks/use-cancel-booking";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import PrepaymentEventExpiredAlert from "components/alerts/payment-event-expired-alert";
import CancelModal from "components/cancel-modal";
import EditBookingModal from "components/edit-booking/edit-booking-modal";
import ErrorBoundary from "components/error-boundary/error-boundary";
import { useBookingIdParam } from "hooks/use-booking-id-param";
import { TrackingEventType } from "logger/event-utils";
import { sendToParent, trackEvent } from "logger/logger";
import { usePageTracking } from "logger/use-page-tracking";
import { PageType } from "logger/page-tracking";
import { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LayoutContext } from "layout/layout-context";
import { getSearchParams } from "search-params";
import { CONTENT_MAX_WIDTH } from "style-variables";
import { ChargeType } from "types/charge-type";
import { WebBooking } from "types/web-booking";
import {
    cancelAllowed,
    hasAvailableWaitListOffers,
    hasBookingPassed,
    hasUnansweredMenuProposal,
    hasUnhandledPaymentEventLink,
    hasWaitListOffers,
    isBookingCanceled,
    isPaymentEventExpired,
    isWaitList,
    isWaitListOfferLocked,
} from "utils/booking";
import BookingStatus from "components/my-booking/booking-status/booking-status";
import BookMoreButton from "components/my-booking/book-more-button";
import BookingOverview from "components/my-booking/booking-overview";
import { BookingSummary } from "components/my-booking/booking-summary/booking-summary";
import CancelButton from "components/my-booking/cancel-booking/cancel-button";
import CancelNotPossibleAlert from "components/my-booking/cancel-not-possible-alert";
import MenuProposalCta from "components/my-booking/menu-proposal-cta";
import BookingActionButton from "components/my-booking/my-booking-action-button";
import MyBookingSkeleton from "components/my-booking/my-booking-skeleton";
import { ErrorView } from "components/pages/error-view";
import MyBookingAlert from "components/my-booking/my-booking-alert";

interface MyBookingLocationState {
    isFinal?: boolean;
    openBookingDetails?: boolean;
    skipValidation?: boolean;
}

export default function MyBooking() {
    const { isLoading: isLoadingBooking, data: webBooking, isError } = useWebBooking();
    const { data: settings, isPending: isLoadingSettings } = useExternalBookingSettings();
    const bookingId = useBookingIdParam();
    const {
        mutate,
        isSuccess: cancelSuccessful,
        isPending: isCancelLoading,
        isError: isCancelError,
    } = useCancelBooking();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const {
        isOpen: isOpenEditBooking,
        onClose: onCloseEditBooking,
        onOpen: onOpenEditBooking,
    } = useDisclosure();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const bookingSummaryRef = useRef<HTMLDivElement>(null);
    const hasHandledOpenBookingDetailsRef = useRef(false);
    const {
        hasMyBookingBackgroundImage,
        isMyBookingBackgroundImageReady,
        shouldShowMyBookingBackgroundImage,
    } = useContext(LayoutContext);

    const possibleToCancel = webBooking && settings ? cancelAllowed(webBooking, settings) : false;
    const location = useLocation() as TypedHistory & { state?: MyBookingLocationState };
    const [isSmallScreen] = useMediaQuery(`(max-width: ${CONTENT_MAX_WIDTH})`);
    const isBookMoreSticky = isSmallScreen ?? false;
    const shouldOpenBookingDetails = Boolean(location.state?.openBookingDetails);

    usePageTracking({
        pageName: "MyBooking",
        pageType: PageType.Page,
        metadata: {
            isCanceled: webBooking ? isBookingCanceled(webBooking) : undefined,
            isWaitList: webBooking ? isWaitList(webBooking) : undefined,
            hasPassed: webBooking ? hasBookingPassed(webBooking) : undefined,
            possibleToCancel,
        },
    });

    useEffect(() => {
        if (!location.state?.openBookingDetails) return;
        if (hasHandledOpenBookingDetailsRef.current) return;
        hasHandledOpenBookingDetailsRef.current = true;

        requestAnimationFrame(() => {
            bookingSummaryRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });

        const { openBookingDetails, ...state } = location.state;
        void openBookingDetails;
        void navigate(`${location.pathname}${location.search}`, { replace: true, state });
    }, [location.pathname, location.search, location.state, navigate]);

    if (isError) return <ErrorView />;
    const isLoadingMyBookingBackground =
        hasMyBookingBackgroundImage && !isMyBookingBackgroundImageReady;
    if (isLoadingSettings || isLoadingBooking || isLoadingMyBookingBackground) {
        return <MyBookingSkeleton />;
    }

    const isFinal = location.state?.isFinal;

    const isCanceled = webBooking ? isBookingCanceled(webBooking) : false;
    const isWaitListBooking = webBooking ? isWaitList(webBooking) : false;
    const hasPassed = webBooking ? hasBookingPassed(webBooking) : false;

    const isOfferLocked = webBooking ? isWaitListOfferLocked(webBooking) : false;
    const hasOffers = webBooking ? hasWaitListOffers(webBooking) : false;
    const hasAvailableOffers = webBooking ? hasAvailableWaitListOffers(webBooking) : false;

    const onCancelBookingClick = () => {
        mutate({ id: bookingId });
        onClose();
        if (webBooking) {
            trackEvent("CancelBookingClicked", {
                guests: webBooking.guests,
                children: webBooking.guestsChildren,
                date: webBooking.start.toISOString(),
                isWaitingList: isWaitListBooking,
            });
        }
        sendToParent({}, TrackingEventType.cancel);
    };

    const { isPaymentLink } = getSearchParams();

    const hasUnhandledPaymentLink = webBooking
        ? hasUnhandledPaymentEventLink(webBooking, isPaymentLink)
        : false;
    const isPaymentLinkExpired = webBooking
        ? isPaymentEventExpired(webBooking, isPaymentLink)
        : false;

    const shouldShowCancelBtn = possibleToCancel && !isFinal && !cancelSuccessful && !isOfferLocked;
    const bookingCanceled = cancelSuccessful || isCanceled;
    const shouldShowBookingSummary = (!bookingCanceled && !hasPassed && !isOfferLocked) || isFinal;
    const shouldShowMenuProposalCta = Boolean(
        webBooking && hasUnansweredMenuProposal(webBooking) && !bookingCanceled && !hasPassed
    );
    const isBookingUnavailable =
        bookingCanceled || hasPassed || isOfferLocked || (hasOffers && !hasAvailableOffers);
    const shouldShowConfirmWaitListBtn =
        hasAvailableOffers && !isBookingUnavailable && !isOfferLocked;
    const shouldHideBoookingOverview = isOfferLocked && !hasPassed && !isFinal;
    const shouldShowActionButton =
        shouldShowConfirmWaitListBtn ||
        (hasUnhandledPaymentLink && !isWaitListBooking) ||
        webBooking?.isInquiryProposal();
    const allowEditBooking =
        settings?.allowEditBooking &&
        webBooking?.isWebBooking() &&
        !webBooking.isActivityBooking() &&
        !webBooking.requiresMenuPayment() &&
        webBooking.chargeType === ChargeType.None &&
        webBooking.nonTableArticles.length === 0;
    return (
        <ErrorBoundary fallback={<ErrorView />}>
            <ScaleFade
                className="scale-fade"
                initialScale={0.9}
                in
                transition={{ enter: { duration: 0.5 } }}
            >
                <Stack
                    spacing={ThemeSpaceVariable.None}
                    px={{ base: ThemeSpaceVariable.Medium, lg: ThemeSpaceVariable.XLarge }}
                    py={ThemeSpaceVariable.Large}
                    w="100%"
                    maxW="36rem"
                >
                    <BookingStatus
                        isFinal={!!isFinal}
                        cancelSuccessful={cancelSuccessful}
                        isCancelError={isCancelError}
                    />
                    {shouldShowMenuProposalCta && (
                        <Box mt={ThemeSpaceVariable.Medium}>
                            <MenuProposalCta />
                        </Box>
                    )}
                    <Stack
                        mt={
                            shouldShowMenuProposalCta
                                ? ThemeSpaceVariable.Large
                                : ThemeSpaceVariable.Medium
                        }
                        spacing={ThemeSpaceVariable.Medium}
                        w="full"
                    >
                        {!shouldHideBoookingOverview && (
                            <BookingOverview disableOverview={!isFinal && !!isBookingUnavailable} />
                        )}
                        <Box
                            alignSelf={{ base: "stretch", sm: "flex-start" }}
                            w={{ base: "100%", sm: "auto" }}
                        >
                            {shouldShowCancelBtn ? (
                                <Stack
                                    direction={{ base: "column", sm: "row" }}
                                    alignItems={{ base: "stretch", sm: "center" }}
                                    spacing={ThemeSpaceVariable.Small}
                                    w="full"
                                >
                                    {allowEditBooking && (
                                        <Box
                                            order={{ base: 1, sm: 2 }}
                                            w={{ base: "full", sm: "auto" }}
                                        >
                                            <Button
                                                onClick={onOpenEditBooking}
                                                variant="secondary"
                                                size="xl"
                                                w={{ base: "full", sm: "auto" }}
                                                data-track-click="mybooking-edit-booking"
                                            >
                                                {t("editBooking.editBooking")}
                                            </Button>
                                        </Box>
                                    )}
                                    <Box
                                        order={{ base: allowEditBooking ? 2 : 1, sm: 1 }}
                                        w={{ base: "full", sm: "auto" }}
                                    >
                                        <CancelButton
                                            onClick={onOpen}
                                            isLoading={isCancelLoading}
                                            trackingId="mybooking-cancel-booking"
                                            contrastMode={
                                                shouldShowMyBookingBackgroundImage
                                                    ? "onImage"
                                                    : "default"
                                            }
                                        />
                                    </Box>
                                </Stack>
                            ) : !isFinal && !isBookingUnavailable && !isPaymentLinkExpired ? (
                                <CancelNotPossibleAlert />
                            ) : (
                                <BookMoreButton sticky={isBookMoreSticky} />
                            )}
                        </Box>
                        {isPaymentLinkExpired && <PrepaymentEventExpiredAlert />}
                        {shouldShowActionButton && <BookingActionButton />}
                        {shouldShowBookingSummary && (
                            <Box ref={bookingSummaryRef}>
                                <BookingSummary defaultOpen={shouldOpenBookingDetails} />
                            </Box>
                        )}
                    </Stack>
                    <MyBookingAlert
                        shouldShowConfirmWaitListBtn={!!shouldShowConfirmWaitListBtn}
                        isBookingCanceled={bookingCanceled}
                    />
                </Stack>
                <CancelModal
                    isOpen={isOpen}
                    handleCancelModalModalClose={onClose}
                    handleCancelModalCancel={onCancelBookingClick}
                />
                <EditBookingModal
                    isOpen={isOpenEditBooking}
                    onClose={onCloseEditBooking}
                    booking={webBooking ?? new WebBooking()}
                />
            </ScaleFade>
        </ErrorBoundary>
    );
}
